import { NestedFilterItem } from '@apeiron/common'
import { NextImage, noForwardProps } from '@apeiron/temp'
import { Box, ButtonBase, styled } from '@mui/material'
import EyeClosedSVG from '@public/icons/icon-eye-closed.svg'
import EyeOpenedSVG from '@public/icons/icon-eye-opened.svg'
import TrashSVG from '@public/icons/icon-trash.svg'
import * as R from 'ramda'
import { FC, ReactElement, useCallback, useMemo } from 'react'

const Container = styled(Box)`
  width: 100%;

  > *:not(:last-child) {
    border-bottom: 1px solid #00000011;
  }
`

const Item = styled(Box)`
  padding-top: 10px;
  display: flex;
  flex-direction: column;

  :hover {
    background-color: #bfbfbf;
  }
`

const ActionButtonContainer = styled(Box)`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-right: 10px;
`

const ActionButton = styled(ButtonBase, noForwardProps)<ColorProps>`
  width: 20px;
`

const Icon = styled(NextImage, noForwardProps)<ColorProps>`
  aspect-ratio: 1;
  width: 100%;

  ${props =>
    props.__hoverRed
      ? `
        :hover {
          filter: invert(52%) sepia(93%) saturate(6616%) hue-rotate(345deg);
        }
      `
      : ''}
`

const FilterItem: FC<FilterItemProps> = props => {
  const {
    item,
    isDeactivated,
    removeItem,
    renderActionItems,
    renderItem,
    toggleItem
  } = props

  const onClickToggle = useCallback(() => {
    toggleItem(item)
  }, [item, toggleItem])

  const onClickRemove = useCallback(() => {
    removeItem(item)
  }, [item, removeItem])

  const component = useMemo(() => renderItem(item), [item, renderItem])

  return (
    <Item>
      <ActionButtonContainer>
        {renderActionItems && renderActionItems(item)}
        <ActionButton onClick={onClickToggle}>
          <Icon
            __hoverRed={isDeactivated}
            src={isDeactivated ? EyeClosedSVG : EyeOpenedSVG}
          />
        </ActionButton>
        <ActionButton onClick={onClickRemove}>
          <Icon __hoverRed src={TrashSVG} />
        </ActionButton>
      </ActionButtonContainer>
      {component}
    </Item>
  )
}

const NestedFilterList: FC<Props> = (props: Props) => {
  const {
    className,
    items,
    removeItem,
    renderActionItems,
    renderItem,
    toggleItem
  } = props

  return !R.isEmpty(items) ? (
    <Container className={className}>
      {items.map((item: NestedFilterItem) => (
        <FilterItem
          item={item}
          isDeactivated={item.disabled}
          key={item.uniqueKey}
          removeItem={removeItem}
          renderActionItems={renderActionItems}
          renderItem={renderItem}
          toggleItem={toggleItem}
        />
      ))}
    </Container>
  ) : (
    <></>
  )
}

type ColorProps = {
  __hoverRed?: boolean
}

type FilterItemProps = {
  item: NestedFilterItem
  isDeactivated: boolean
  removeItem: (item: NestedFilterItem) => void
  renderActionItems?: (item: NestedFilterItem) => ReactElement
  renderItem: (item: NestedFilterItem) => ReactElement
  toggleItem: (item: NestedFilterItem) => void
}

type Props = {
  className?: string
  items: NestedFilterItem[]
  removeItem: (item: NestedFilterItem) => void
  renderActionItems?: (item: NestedFilterItem) => ReactElement
  renderItem: (item: NestedFilterItem) => ReactElement
  toggleItem: (item: NestedFilterItem) => void
}

export default NestedFilterList
